import React, { useState } from 'react';
import { premium_badge } from '../../../../assets/updated_assests/assets';
// Adjust path as needed
import {
    useAddPrimaryBadgeMutation, useAddSpecialBadgeMutation, usePrimaryBadgesQuery,
    useSpecialBadgesQuery, useDeleteBadgesMutation
} from '../../../../services/Api';
import Swal from 'sweetalert2'
import { BASE_URL_IMAGE } from '../../../../services/ApiEndpoints';

const BadgeModal = ({ open, onClose, onSave, isPrimary, isLoading }) => {
    const [name, setName] = useState('');
    const [file, setFile] = useState(null);
    const [points, setPoints] = useState('');
    const [isStarting, setIsStarting] = useState(false);

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-lg relative">
                <button className="absolute top-2 right-3 text-xl" onClick={onClose}>&times;</button>
                <h3 className="font-bold text-lg mb-4">{isPrimary ? 'Add Primary Badge' : 'Add Special Badge'}</h3>
                <form
                    onSubmit={async e => {
                        e.preventDefault();
                        await onSave({ name, file, points, isStarting });
                        setName('');
                        setFile(null);
                        setPoints('');
                        setIsStarting(false);
                        onClose();
                    }}
                >
                    <div className="mb-3">
                        <label className="block text-sm font-medium mb-1">Badge Name</label>
                        <input
                            type="text"
                            className="border rounded w-full p-2"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-medium mb-1">Upload File</label>
                        <input
                            type="file"
                            className="border rounded w-full p-2"
                            onChange={e => setFile(e.target.files[0])}
                            required
                        />
                    </div>
                    {isPrimary && (
                        <>
                            <div className="mb-3">
                                <label className="block text-sm font-medium mb-1">Points Required</label>
                                <input
                                    type="number"
                                    className="border rounded w-full p-2"
                                    value={points}
                                    onChange={e => setPoints(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3 flex items-center">
                                <input
                                    type="checkbox"
                                    id="starting"
                                    checked={isStarting}
                                    onChange={e => setIsStarting(e.target.checked)}
                                    className="mr-2"
                                />
                                <label htmlFor="starting" className="text-sm">Set as starting Badge</label>
                            </div>
                        </>
                    )}
                    <button
                        type="submit"
                        className="w-full bg-bgprimary text-dark font-bold py-2 rounded mt-2"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Saving...' : 'Save'}
                    </button>
                </form>
            </div>
        </div>
    );
};
const BadgesPage = () => {
    const [showPrimaryModal, setShowPrimaryModal] = useState(false);
    const [showSpecialModal, setShowSpecialModal] = useState(false);

    // RTK Query hooks
    const { data: primaryBadges = [], refetch: refetchPrimary } = usePrimaryBadgesQuery();
    const { data: specialBadges = [], refetch: refetchSpecial } = useSpecialBadgesQuery();
    const [addPrimaryBadge, { isLoading: isPrimaryLoading }] = useAddPrimaryBadgeMutation();
    const [addSpecialBadge, { isLoading: isSpecialLoading }] = useAddSpecialBadgeMutation();
    const [deleteBadges] = useDeleteBadgesMutation();

    // FormData helper for file upload
    const buildFormData = (badge, isPrimary) => {
        const formData = new FormData();
        formData.append('name', badge.name);
        formData.append('badgeImage', badge.file);
        if (isPrimary) {
            formData.append('points', badge.points);
            formData.append('isStarting', badge.isStarting);
        }
        return formData;
    };

    const handleDeleteBadge = async (badgeId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
        });
        if (result.isConfirmed) {
            try {
                const res = await deleteBadges(badgeId).unwrap();
                refetchPrimary();
                Swal.fire('Deleted!', res?.message || 'Badge has been deleted.', 'success');
            } catch (error) {
                Swal.fire('Error!', error?.data?.message || 'Failed to delete badge.', 'error');
            }
        }
    };

    const handleAddPrimary = async badge => {
        try {
            const res = await addPrimaryBadge({ data: buildFormData(badge, true) }).unwrap();
            refetchPrimary();
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: res?.message || 'Primary badge added successfully!',
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.data?.message || 'Failed to add primary badge.',
            });
        }
    };

    const handleAddSpecial = async badge => {
        try {
            const res = await addSpecialBadge({ data: buildFormData(badge, false) }).unwrap();
            refetchSpecial();
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: res?.message || 'Special badge added successfully!',
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.data?.message || 'Failed to add special badge.',
            });
        }
    };

    return (
        <div className="min-h-screen max-w-4xl mx-auto  p-4">
            <div className="bg-[#d9dab2] px-8 py-3 flex items-center">
                <span className="text-3xl mr-3">
                    <img src={premium_badge} className='w-10' alt="" />
                </span>
                <h1 className="text-3xl font-bold font-roboto-mono italic text-black">Badges</h1>
            </div>
            <div className=" mx-auto mt-6">
                {/* Primary Badges */}
                <div>
                    <div className="flex items-center mb-2">
                        <span className="text-xl font-bold italic font-roboto-mono text-black mr-3">Primary Bages</span>
                        <button
                            className="bg-bgprimary text-black font-bold px-3 py-2 rounded text-sm ml-2 border border-bgprimary"
                            onClick={() => setShowPrimaryModal(true)}
                        >
                            Add+
                        </button>
                    </div>
                    <div className="flex gap-4 mb-4">
                        {primaryBadges?.data?.map((badge, idx) => (
                            <div key={idx} className="relative w-16 h-16 bg-[#edeccf] rounded flex items-center justify-center border border-[#d9dab2]">
                                {badge.badgeImage ? (
                                    <img src={`${BASE_URL_IMAGE}${badge.badgeImage}`} alt={badge.name} className="w-12 h-12 object-contain" />
                                ) : (
                                    <span className="text-xs text-gray-400">No Image</span>
                                )}
                                <button
                                    className="absolute top-0 right-0 text-red-600 hover:text-red-800 p-1"
                                    title="Delete"
                                    onClick={() => handleDeleteBadge(badge._id)}
                                >
                                    {/* Simple trash icon, replace with your SVG if needed */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ))}

                        {/* {[...Array(4 - primaryBadges.length)].map((_, idx) => (
                            <div key={idx} className="w-16 h-16 bg-[#edeccf] rounded border border-[#d9dab2]" />
                        ))} */}
                    </div>
                </div>
                {/* Special Badges */}
                <div>
                    <div className="flex items-center mb-2">
                        <span className="text-xl font-bold italic font-roboto-mono text-black mr-3">Special Bages</span>
                        <button
                            className="bg-bgprimary text-black font-bold px-3 py-2 rounded text-sm ml-2 border border-bgprimary"
                            onClick={() => setShowSpecialModal(true)}
                        >
                            Add+
                        </button>
                    </div>
                    <div className="flex gap-4 mb-4">
                        {specialBadges?.data?.map((badge, idx) => (
                            <div key={idx} className="relative w-16 h-16 bg-[#edeccf] rounded flex items-center justify-center border border-[#d9dab2]">
                                {badge.badgeImage ? (
                                    <img src={`${BASE_URL_IMAGE}${badge.badgeImage}`} alt={badge.name} className="w-12 h-12 object-contain" />
                                ) : (
                                    <span className="text-xs text-gray-400">No Image</span>
                                )}
                                <button
                                    className="absolute top-0 right-0 text-red-600 hover:text-red-800 p-1"
                                    title="Delete"
                                    onClick={() => handleDeleteBadge(badge._id)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                        {/* {[...Array(4 - specialBadges.length)].map((_, idx) => (
                            <div key={idx} className="w-16 h-16 bg-[#edeccf] rounded border border-[#d9dab2]" />
                        ))} */}
                    </div>
                </div>
            </div>
            {/* Modals */}
            <BadgeModal
                open={showPrimaryModal}
                onClose={() => setShowPrimaryModal(false)}
                onSave={handleAddPrimary}
                isPrimary={true}
                isLoading={isPrimaryLoading}
            />
            <BadgeModal
                open={showSpecialModal}
                onClose={() => setShowSpecialModal(false)}
                onSave={handleAddSpecial}
                isPrimary={false}
                isLoading={isSpecialLoading}
            />
        </div>
    );
};

export default BadgesPage;